import React, { useCallback, useState } from 'react'
import { isNil } from 'lodash'
import { Button, DialogActions, DialogContent, Box, Table, TableBody } from '@mui/material'
import { Warning } from '@mui/icons-material'
import { UpdateQueryOptions } from '@apollo/client'
import * as Yup from 'yup'
import { EngagementType, PatientEngagementIndicator } from 'src/graphql.generated'
import { GraphQLForm } from 'src/form'
import { ClosableDialogTitle } from 'src/legacy/components/mui'
import { FormatDate } from 'src/date'
import { GetPatientHistoryDocument, useGetPatientHistoryQuery } from 'src/legacy/lib/graphql/queries.generated'
import Baseline from './Baseline'
import Engagement from './Engagement'
import ProgramEndResult from './ProgramEndResult'
import PercentageChange from './PercentageChange'
import { ParticipantResultsHeader, ParticipantResultsTextRow } from './ParticipantResultsTable'
import { GetFullPatientDocument, useSetPatientProgramEndMutation } from '../../patientQueries.generated'
import ConfirmCareTeamChange from '../../SetCareTeamForm/ConfirmCareTeamChange'
import useLoadCoachProfiles from '../../SetCareTeamForm/useLoadCoachProfiles'
import { useLoadSetCareTeamData } from '../../SetCareTeamForm/useLoadSetCareTeamData'
import { ProgramEndMetrics, ProgramEndMetricsSchema } from './programEndMetricSchemas'
import FollowUpProgramSelect from './FollowUpProgramSelect'
import SelectCareTeamToKeep from './SelectCareTeamToKeep'
import TerminateAdvancedPatientDialog from './TerminateAdvancedPatientDialog'
import useFollowUpProgram from './useFollowUpProgram'

type ProgramEndForm = {
  uid: string
  engagement: EngagementType
  programEndMetrics: ProgramEndMetrics
  followUpProgramID?: string
  careteamCoachIDs?: string[]
  primaryCoachID?: string
}

const ProgramEndSchema = (uid: string) =>
  Yup.object({
    uid: Yup.string().default(uid).required(),
    engagement: Yup.string()
      .oneOf([EngagementType.Completer, EngagementType.Dropout, EngagementType.NoShow])
      .required(),
    programEndMetrics: ProgramEndMetricsSchema.required().default({}),
    followUpProgramID: Yup.string(),
    careteamCoachIDs: Yup.array().of(Yup.string().required()),
    primaryCoachID: Yup.string(),
  }).required()

export type Props = {
  close: () => void
  cancel: () => void
  engagement: PatientEngagementIndicator
  lastWeekNbr: number
  programStartDate: string
  programEndMetrics: Array<keyof ProgramEndMetrics>
  programLoaded: boolean
  uid: string
  terminateProgram: boolean
}

const MissingProgramEndMetrics = () => (
  <>
    <ParticipantResultsHeader label="No Program Metrics For Program" />
    <ParticipantResultsTextRow
      label="Please try reloading the page and contact support if the problem persists"
      content={
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Warning fontSize="small" color="error" />
        </Box>
      }
    />
  </>
)

const baselinesForAllMetricsAvailable = (endMetricsInProgram: ProgramEndMetrics) =>
  Object.keys(endMetricsInProgram)
    .map((metricKey) => {
      const metricValues = endMetricsInProgram[metricKey as keyof ProgramEndMetrics]!
      return metricValues.baseline
    })
    .every((value) => !isNil(value))

const isDisabled = (endMetricsInProgram: ProgramEndMetrics) => !baselinesForAllMetricsAvailable(endMetricsInProgram)

const EndProgram = ({
  cancel,
  close,
  engagement,
  uid,
  programStartDate,
  lastWeekNbr,
  programEndMetrics,
  programLoaded,
  terminateProgram,
}: Props) => {
  const { data, loading } = useGetPatientHistoryQuery({ variables: { uid } })
  const { followUpProgram, loading: followUpProgramLoading } = useFollowUpProgram(uid)
  const coachProfiles = useLoadCoachProfiles()
  const { participantsWithCareTeams, loading: careTeamLoading } = useLoadSetCareTeamData([uid])
  const oldCareTeam = participantsWithCareTeams[0]?.oldCareTeam
  const [showCareteamConfirmation, setShowCareteamConfirmation] = useState(false)

  const isAdvancedPatient = Boolean(followUpProgram)

  const { patientHistory } = data || {}

  const [setPatientProgramEnd] = useSetPatientProgramEndMutation({
    refetchQueries: [GetFullPatientDocument, GetPatientHistoryDocument],
  })
  const handleSubmit = useCallback(
    async (opts: UpdateQueryOptions<ProgramEndForm>): Promise<void> => {
      const { variables } = opts!
      const { careteamCoachIDs, primaryCoachID, followUpProgramID, ...programEnd } = variables!

      await setPatientProgramEnd({
        variables: {
          ...programEnd,
          followUpProgramID: followUpProgramID || undefined,
          newCareteam:
            followUpProgramID && primaryCoachID
              ? { primaryCoachID, coachIDs: [primaryCoachID, ...(careteamCoachIDs ?? [])] }
              : undefined,
        },
      })
    },
    [setPatientProgramEnd]
  )

  return (
    <GraphQLForm
      onSubmit={handleSubmit}
      onSuccess={close}
      validationSchema={ProgramEndSchema(uid)}
      render={({ isSubmitting, values }) => {
        if (showCareteamConfirmation) {
          return (
            <ConfirmCareTeamChange
              close={close}
              handleReturn={() => setShowCareteamConfirmation(false)}
              newCareTeam={{
                primaryCoachID: values.primaryCoachID,
                careteamCoachIDs: values.careteamCoachIDs ?? [],
              }}
              oldParticipantCareTeams={participantsWithCareTeams}
              coachProfiles={coachProfiles}
              isSubmitting={isSubmitting}
            />
          )
        }

        return (
          <>
            <ClosableDialogTitle onClose={close}>Confirm Program Results</ClosableDialogTitle>
            <DialogContent>
              <Table size="small">
                <TableBody>
                  <ParticipantResultsHeader label="Program" />
                  <ParticipantResultsTextRow label="Start" content={<FormatDate value={programStartDate} />} />
                  <Engagement
                    type={engagement.type}
                    completedIntros={engagement.completedIntros}
                    lastWeekNbr={lastWeekNbr}
                  />
                  {!programLoaded && <MissingProgramEndMetrics />}
                  {programEndMetrics.length > 0 &&
                    programEndMetrics.map((metric) => (
                      <React.Fragment key={metric}>
                        <ParticipantResultsHeader label={metric} />
                        <Baseline
                          type={metric}
                          data={{ patientHistory, loading }}
                          programStartDate={programStartDate}
                        />
                        <ProgramEndResult
                          type={metric}
                          data={{ patientHistory, loading }}
                          programStartDate={programStartDate}
                          lastWeekNbr={lastWeekNbr}
                        />
                        <PercentageChange
                          isLoading={loading}
                          baseline={values.programEndMetrics[metric]?.baseline}
                          programEndResult={values.programEndMetrics[metric]?.programEnd}
                        />
                      </React.Fragment>
                    ))}
                </TableBody>
              </Table>

              <Box pt={3} display="flex" justifyContent="center">
                Note: The results will be used in billing and reporting.
              </Box>

              {followUpProgram && (
                <Box pt={3}>
                  <FollowUpProgramSelect followUpProgram={followUpProgram} terminateProgram={terminateProgram} />
                  <SelectCareTeamToKeep
                    followUpProgramID={values.followUpProgramID}
                    oldCareTeam={oldCareTeam}
                    coachProfiles={coachProfiles}
                  />
                </Box>
              )}
            </DialogContent>
            <DialogActions>
              <Button color="inherit" onClick={cancel}>
                Cancel
              </Button>
              {isAdvancedPatient && terminateProgram && !values.followUpProgramID ? (
                <TerminateAdvancedPatientDialog
                  followUpProgram={followUpProgram!}
                  oldCareTeam={oldCareTeam}
                  coachProfiles={coachProfiles}
                  onTransition={() => setShowCareteamConfirmation(true)}
                >
                  {(open: () => void) => (
                    <Button
                      color="primary"
                      disabled={
                        loading ||
                        followUpProgramLoading ||
                        careTeamLoading ||
                        isSubmitting ||
                        !programLoaded ||
                        isDisabled(values.programEndMetrics)
                      }
                      onClick={open}
                    >
                      Confirm
                    </Button>
                  )}
                </TerminateAdvancedPatientDialog>
              ) : (
                <Button
                  color="primary"
                  disabled={
                    loading ||
                    followUpProgramLoading ||
                    careTeamLoading ||
                    isSubmitting ||
                    !programLoaded ||
                    isDisabled(values.programEndMetrics)
                  }
                  onClick={values.followUpProgramID ? () => setShowCareteamConfirmation(true) : undefined}
                  type={values.followUpProgramID ? 'button' : 'submit'}
                >
                  Confirm
                </Button>
              )}
            </DialogActions>
          </>
        )
      }}
    />
  )
}

export default EndProgram
